import "@/App.css";
import "@/redesign.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import HomePage from "@/pages/HomePage";
import SearchPage from "@/pages/SearchPage";
import PropertyPage from "@/pages/PropertyPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CheckoutPage from "@/pages/CheckoutPage";
import TravelerDashboard from "@/pages/TravelerDashboard";
import HostDashboard from "@/pages/HostDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import MessagesPage from "@/pages/MessagesPage";
import HelpPage from "@/pages/HelpPage";
import { OnboardingTour } from "@/components/OnboardingTour";
import { BottomNav } from "@/components/BottomNav";
import CityPage from "@/pages/CityPage";
import TransportPage from "@/pages/TransportPage";
import RestaurantsPage from "@/pages/RestaurantsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import PartnerLayout from "@/pages/partner/PartnerLayout";
import PartnerOverview from "@/pages/partner/PartnerOverview";
import PartnerListings from "@/pages/partner/PartnerListings";
import PartnerListingEditor from "@/pages/partner/PartnerListingEditor";
import PartnerBookings from "@/pages/partner/PartnerBookings";
import PartnerCalendar from "@/pages/partner/PartnerCalendar";
import PartnerFinance from "@/pages/partner/PartnerFinance";
import PartnerSettings from "@/pages/partner/PartnerSettings";
import PartnerPromotions from "@/pages/partner/PartnerPromotions";
import ReviewsPage from "@/pages/ReviewsPage";
import AdminPartnerCenter from "@/pages/admin/AdminPartnerCenter";
import TransportServicePage from "@/pages/TransportServicePage";
import AdvertiserPortal from "@/pages/advertiser/AdvertiserPortal";
import AdvertiserPaymentSuccessPage from "@/pages/advertiser/AdvertiserPaymentSuccessPage";
import AdminAdvertiserPage from "@/pages/admin/AdminAdvertiserPage";

function Protected({ children, roles }) {
  const { user, checking } = useAuth();
  const location = useLocation();
  if (checking) return <div className="page-loader" data-testid="session-loading">kamerbooking</div>;
  if (!user) return <Navigate to="/login" state={{ next: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/espace" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  const authPage = ["/login", "/register", "/connexion", "/forgot-password", "/reset-password"].includes(location.pathname);
  const privateWorkspace = location.pathname.startsWith("/partenaire") || location.pathname.startsWith("/annonceur") || location.pathname.startsWith("/admin") || location.pathname.startsWith("/messages");
  return <div className="app-shell">
    {!authPage && <Header />}
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/recherche" element={<SearchPage />} />
      <Route path="/hebergement/:id" element={<PropertyPage />} />
      <Route path="/ville/:city" element={<CityPage />} />
      <Route path="/transport/:id" element={<Protected><TransportPage /></Protected>} />
      <Route path="/services/transport" element={<TransportServicePage />} />
      <Route path="/services/restauration" element={<RestaurantsPage />} />
      <Route path="/paiement/succes" element={<Protected><PaymentSuccessPage /></Protected>} />
      <Route path="/paiement/annule" element={<Protected><PaymentCancelPage /></Protected>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/connexion" element={<Navigate to="/login" replace />} />
      <Route path="/reservation/:id" element={<CheckoutPage />} />
      <Route path="/espace" element={<Protected><TravelerDashboard /></Protected>} />
      <Route path="/espace/avis" element={<Protected><ReviewsPage /></Protected>} />
      <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
      <Route path="/aide" element={<HelpPage />} />
      <Route path="/hote" element={<Protected roles={["host", "admin"]}><HostDashboard /></Protected>} />
      <Route path="/partenaire" element={<Protected roles={["host", "admin"]}><PartnerLayout /></Protected>}>
        <Route index element={<PartnerOverview />} />
        <Route path="annonces" element={<PartnerListings />} />
        <Route path="annonces/nouvelle" element={<PartnerListingEditor />} />
        <Route path="annonces/:id" element={<PartnerListingEditor />} />
        <Route path="reservations" element={<PartnerBookings />} />
        <Route path="calendrier" element={<PartnerCalendar />} />
        <Route path="finance" element={<PartnerFinance />} />
        <Route path="promotions" element={<PartnerPromotions />} />
        <Route path="parametres" element={<PartnerSettings />} />
      </Route>
      <Route path="/annonceur" element={<Protected roles={["advertiser", "admin"]}><AdvertiserPortal /></Protected>} />
      <Route path="/annonceur/paiement/succes" element={<Protected roles={["advertiser", "admin"]}><AdvertiserPaymentSuccessPage /></Protected>} />
      <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
      <Route path="/admin/partenaires" element={<Protected roles={["admin"]}><AdminPartnerCenter /></Protected>} />
      <Route path="/admin/annonceurs" element={<Protected roles={["admin"]}><AdminAdvertiserPage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <Toaster richColors position="top-right" />
    {!authPage && !privateWorkspace && <OnboardingTour />}
    {!authPage && <BottomNav />}
  </div>;
}

export default function App() {
  return <BrowserRouter><AppProvider><AuthProvider><AppRoutes /></AuthProvider></AppProvider></BrowserRouter>;
}